import React, { useState } from 'react';
import { LiquidityEvent, Currency } from '../types';
import { Card, CardHeader, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { Input, Label, Select } from './ui/Forms';
import { PlusCircle, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { CURRENCIES } from '../utils/countries';
interface LiquidityEventFormProps {
  onAdd: (event: Omit<LiquidityEvent, 'id'>) => void;
  baseCurrency?: Currency;
  initialValues?: {
    name?: string;
    currency?: Currency;
    type?: LiquidityEvent['type'];
    isPositive?: boolean;
    startYear?: string;
  };
}
export function LiquidityEventForm({
  onAdd,
  baseCurrency = 'USD',
  initialValues
}: LiquidityEventFormProps) {
  const [name, setName] = useState(initialValues?.name ?? '');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<Currency>(initialValues?.currency ?? baseCurrency);
  const [type, setType] = useState<LiquidityEvent['type']>(initialValues?.type ?? 'one-time');
  const [startYear, setStartYear] = useState(initialValues?.startYear ?? '1');
  const [endYear, setEndYear] = useState('');
  const [isPositive, setIsPositive] = useState(initialValues?.isPositive ?? true);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !amount || isNaN(Number(amount))) return;
    if (!startYear || isNaN(Number(startYear))) return;
    const finalAmount = Number(amount) * (isPositive ? 1 : -1);
    onAdd({
      name,
      amount: finalAmount,
      currency,
      type,
      startYear: Number(startYear),
      endYear: endYear ? Number(endYear) : undefined
    });
    setName('');
    setAmount('');
    setStartYear('1');
    setEndYear('');
  };
  return <Card data-id="element-704">
      <CardHeader title="Add Liquidity Event" subtitle="Future cash flows (e.g. property sale, inheritance, tuition)" data-id="element-705" />
      <CardContent data-id="element-706">
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-707">
          <div data-id="element-708">
            <Label data-id="element-709">Event Name</Label>
            <Input placeholder="e.g. Sell property, College tuition" value={name} onChange={e => setName(e.target.value)} required data-id="element-710" />
          </div>

          <div data-id="element-711">
            <Label data-id="element-712">Direction</Label>
            <div className="flex bg-slate-100 p-1 rounded-lg" data-id="element-713">
              <button type="button" className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${isPositive ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setIsPositive(true)} data-id="element-714">
                <ArrowUpRight className="w-4 h-4" data-id="element-715" /> Inflow (+)
              </button>
              <button type="button" className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${!isPositive ? 'bg-white text-rose-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setIsPositive(false)} data-id="element-716">
                <ArrowDownRight className="w-4 h-4" data-id="element-717" /> Outflow (-)
              </button>
            </div>
          </div>

          <div data-id="element-718">
            <Label data-id="element-719">Frequency</Label>
            <Select value={type} onChange={e => setType(e.target.value as LiquidityEvent['type'])} options={[{
            value: 'one-time',
            label: 'One-time'
          }, {
            value: 'monthly',
            label: 'Monthly'
          }, {
            value: 'yearly',
            label: 'Yearly'
          }]} data-id="element-720" />
          </div>

          <div className="grid grid-cols-3 gap-4" data-id="element-721">
            <div className="col-span-2" data-id="element-722">
              <Label data-id="element-723">Amount</Label>
              <Input type="number" min="0" step="0.01" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} required data-id="element-724" />
            </div>
            <div data-id="element-725">
              <Label data-id="element-726">Currency</Label>
              <Select searchable value={currency} onChange={e => setCurrency(e.target.value as Currency)} options={CURRENCIES.map(c => ({
              value: c.code,
              label: `${c.flag} ${c.code} (${c.symbol})`
            }))} data-id="element-727" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-728">
            <div data-id="element-729">
              <Label data-id="element-730">Start Year (from now)</Label>
              <Input type="number" min="0" step="1" value={startYear} onChange={e => setStartYear(e.target.value)} required data-id="element-731" />
            </div>
            {type !== 'one-time' && <div data-id="element-732">
                <Label data-id="element-733">End Year (optional)</Label>
                <Input type="number" min={startYear} step="1" placeholder="Forever" value={endYear} onChange={e => setEndYear(e.target.value)} data-id="element-734" />
              </div>}
          </div>

          <Button type="submit" className="w-full mt-2" data-id="element-735">
            <PlusCircle className="w-4 h-4 mr-2" data-id="element-736" />
            Add Event
          </Button>
        </form>
      </CardContent>
    </Card>;
}